<template>
	<v-card-text class="pa-6 pa-md-8">
		<v-row v-if="producto">
			<!-- Gallery Column -->
			<v-col cols="12" md="4" class="text-center">
				<div class="quick-zoom-wrap pa-2 mb-3">
					<product-zoom :src="producto.image_url" :alt="producto.name" height="260px"></product-zoom>
				</div>
				<v-slide-group
					v-if="producto.images && producto.images.length > 1"
					v-model="selectedImageIndex"
					show-arrows
					center-active
					mandatory
					class="py-1"
				>
					<template v-slot:prev>
						<v-btn icon x-small @click.stop="prevImage">
							<v-icon>mdi-chevron-left</v-icon>
						</v-btn>
					</template>
					<template v-slot:next>
						<v-btn icon x-small @click.stop="nextImage">
							<v-icon>mdi-chevron-right</v-icon>
						</v-btn>
					</template>
					<v-slide-item
						v-for="(img, idx) in producto.images"
						:key="img.id || idx"
						v-slot:default="{ active, toggle }"
						class="px-1"
					>
						<div
							class="quick-thumbnail"
							:class="{ active: active }"
							@click="
								selectImage(idx);
								toggle();
							"
						>
							<v-img :src="img.url" height="48px" width="48px" contain class="rounded"></v-img>
						</div>
					</v-slide-item>
				</v-slide-group>
			</v-col>

			<!-- Details Column -->
			<v-col cols="12" md="5" class="px-md-4">
				<div class="mb-1" v-if="producto.categoria">
					<span class="category-pill-sm">{{ producto.categoria.nombre }}</span>
				</div>

				<h2 class="quick-title text-slate-900 font-weight-bold mb-2">{{ producto.name }}</h2>

				<div class="d-flex align-center flex-wrap mb-3 meta-badges">
					<div class="meta-tag mr-3 mb-1">
						<span class="caption text-secondary-custom">{{ "store.reference" | trans }}:</span>
						<strong class="caption ml-1 text-slate-800">{{ producto.referencia }}</strong>
					</div>
					<div class="meta-tag mr-3 mb-1">
						<span class="caption text-secondary-custom">{{ "store.condition" | trans }}:</span>
						<span class="caption ml-1 text-success font-weight-bold">{{ "store.new" | trans }}</span>
					</div>
					<div class="meta-tag mb-1" v-if="producto.mostrar_stock">
						<span class="stock-pill-sm in-stock"> {{ "store.stock" | trans }}: {{ producto.stock | number }} </span>
					</div>
				</div>

				<v-divider class="my-3"></v-divider>

				<div class="quick-desc text-slate-700 mb-4" style="max-height: 140px; overflow-y: auto">
					<div v-html="producto.description" v-if="producto.description"></div>
					<div class="text-caption text-secondary-custom font-italic" v-else>{{ "store.no_description" | trans }}</div>
				</div>

				<!-- Share section -->
				<div class="d-flex align-center flex-wrap pt-2">
					<span class="caption font-weight-bold text-secondary-custom mr-3">{{ "store.share_on" | trans }}:</span>
					<ShareNetwork
						network="whatsapp"
						:url="`${api.url.replace('public', 'store')}#/product/${encodeURIComponent(producto.referencia)}`"
						:title="producto.name"
						:description="producto.description"
						class="mr-1"
					>
						<v-btn icon x-small color="#25D366"><v-icon small>mdi-whatsapp</v-icon></v-btn>
					</ShareNetwork>
					<ShareNetwork
						network="facebook"
						:url="`${api.url.replace('public', 'store')}#/product/${encodeURIComponent(producto.referencia)}`"
						:title="producto.name"
						:description="producto.description"
						class="mr-1"
					>
						<v-btn icon x-small color="#1877F2"><v-icon small>mdi-facebook</v-icon></v-btn>
					</ShareNetwork>
					<ShareNetwork
						network="email"
						:url="`${api.url.replace('public', 'store')}#/product/${encodeURIComponent(producto.referencia)}`"
						:title="producto.name"
						:description="producto.description"
					>
						<v-btn icon x-small color="#EA4335"><v-icon small>mdi-email-outline</v-icon></v-btn>
					</ShareNetwork>
				</div>
			</v-col>

			<!-- Action Column -->
			<v-col cols="12" md="3" class="action-column pa-4 rounded-xl">
				<div class="mb-3" v-if="producto.mostrar_precio">
					<span class="caption text-secondary-custom d-block mb-1">{{ "price" | trans }}</span>
					<div class="quick-price font-weight-bold text-slate-900">
						{{ (producto.precio + (producto.precio * producto.impuesto) / 100) | money }}
					</div>
				</div>

				<div class="mb-3">
					<label class="caption font-weight-bold text-slate-800 d-block mb-1">{{ "quantity" | trans }}</label>
					<div class="quantity-stepper d-flex align-center">
						<v-btn icon small @click="addProduct(-1)" class="stepper-btn" :disabled="quantity <= 1">
							<v-icon small>mdi-minus</v-icon>
						</v-btn>
						<v-text-field
							v-model.number="quantity"
							type="number"
							min="1"
							dense
							flat
							hide-details
							solo
							class="stepper-input text-center"
						></v-text-field>
						<v-btn icon small @click="addProduct(1)" class="stepper-btn">
							<v-icon small>mdi-plus</v-icon>
						</v-btn>
					</div>
				</div>

				<stock-selector class="my-2" :product="producto" v-model="stockSelected"></stock-selector>

				<v-btn
					block
					large
					depressed
					color="primary"
					class="rounded-pill mb-2 text-capitalize"
					:disabled="producto.advanced_stock && producto.advanced_stock.length > 0 && (!stockSelected || quantity <= 0)"
					@click="addToCart"
				>
					<v-icon left small>mdi-cart-plus</v-icon>
					{{ "store.add_to_cart" | trans }}
				</v-btn>

				<v-btn
					block
					outlined
					color="primary"
					class="rounded-pill text-capitalize"
					:to="`/product/${encodeURIComponent(producto.referencia)}`"
				>
					<v-icon left small>mdi-open-in-new</v-icon>
					{{ "store.go_to_page" | trans }}
				</v-btn>
			</v-col>
		</v-row>
	</v-card-text>
</template>

<script>
import StockSelector from "@/components/StockSelector";
import ProductZoom from "@/components/ProductZoom";

export default {
	name: "ProductDescription",
	components: { StockSelector, ProductZoom },
	async mounted() {
		this.settings = await this.api.loadSettings();
	},
	data() {
		return {
			settings: {
				collection: {},
			},
			producto: {},
			stockSelected: null,
			quantity: 1,
			selectedImageIndex: 0,
		};
	},
	methods: {
		selectImage(index) {
			this.selectedImageIndex = index;
			if (this.producto && this.producto.images && this.producto.images[index]) {
				this.producto.image_url = this.producto.images[index].url;
			}
		},
		nextImage() {
			if (!this.producto || !this.producto.images || !this.producto.images.length) return;
			const nextIdx = (this.selectedImageIndex + 1) % this.producto.images.length;
			this.selectImage(nextIdx);
		},
		prevImage() {
			if (!this.producto || !this.producto.images || !this.producto.images.length) return;
			const prevIdx = (this.selectedImageIndex - 1 + this.producto.images.length) % this.producto.images.length;
			this.selectImage(prevIdx);
		},
		addProduct(qty = 1) {
			this.quantity += qty;
			if (this.quantity < 1) this.quantity = 1;
		},
		addToCart() {
			this.$set(this.producto, "cantidad_pedidos", this.quantity);
			this.api.cart.addToCart(this.producto, false, this.stockSelected);
		},
	},
	props: ["product"],
	watch: {
		product: {
			immediate: true,
			async handler() {
				this.producto = this.product;
				if (this.producto && this.producto.id) {
					this.producto = await this.api.load(`productos/${this.producto.id}?include=images,categoria`);
					this.selectedImageIndex = 0;
					this.producto.impuesto = parseFloat(this.producto.impuesto);
					if (!this.producto.images.find((i) => i.id == this.producto.image_id)) {
						this.producto.images.unshift({
							id: this.producto.image_id,
							url: this.producto.image_url,
						});
					}
					var item = this.api.cart.find((i) => i.id == this.producto.id);
					if (item) this.quantity = item.cantidad_pedidos;
				}
			},
		},
	},
};
</script>

<style scoped>
.quick-zoom-wrap {
	background-color: #f8fafc;
	border-radius: 12px;
	border: 1px solid #f1f5f9;
}

.quick-thumbnail {
	padding: 2px;
	border-radius: 6px;
	border: 2px solid transparent;
	cursor: pointer;
	background-color: #f8fafc;
	transition: all 0.15s ease;
}

.quick-thumbnail:hover {
	border-color: #cbd5e1;
}

.quick-thumbnail.active {
	border-color: #3b82f6;
	background-color: #ffffff;
}

.quick-title {
	font-size: 1.25rem;
	line-height: 1.3;
	color: #0f172a;
}

.quick-desc {
	font-size: 0.875rem;
	line-height: 1.5;
}

.action-column {
	background-color: #f8fafc;
	border: 1px solid #eef2f6;
}

.quick-price {
	font-size: 1.35rem;
	color: #0f172a;
}

.category-pill-sm {
	font-size: 0.72rem;
	font-weight: 600;
	padding: 2px 8px;
	border-radius: 4px;
	background-color: #eff6ff;
	color: #2563eb;
}

.stock-pill-sm {
	font-size: 0.72rem;
	font-weight: 600;
	padding: 2px 8px;
	border-radius: 4px;
	background-color: #f1f5f9;
	color: #475569;
}

.stock-pill-sm.in-stock {
	background-color: #ecfdf5;
	color: #059669;
}

.quantity-stepper {
	background-color: #ffffff;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	padding: 2px 4px;
}

.stepper-btn {
	color: #475569;
}

.stepper-input {
	max-width: 50px;
	font-weight: 600;
}

.stepper-input input {
	text-align: center !important;
}

.text-slate-900 {
	color: #0f172a;
}

.text-slate-800 {
	color: #1e293b;
}

.text-slate-700 {
	color: #334155;
}

.text-secondary-custom {
	color: #64748b;
}
</style>
